import { Instant, IsoFields, LocalDateTime, ZoneId } from "@js-joda/core";
import { Dashboard } from "api/model/dashboard/Dashboard";
import { FreeTextDateVariable } from "components/pages/dashboard/components/free-text/FreeTextConfig";
import { DweenTimeUnitEnum } from "utils/enums";
import { BROWSER_ZONE_ID, JodaTimeUtils, toNativeDate } from "utils/time-utils";
import { DATE_PATTERN_MINUTES } from "./constants";

export type VariableReplacementContext = {
  dashboard?: Dashboard;
  referenceTime?: string;
  instantReferenceTime?: Instant;
  zoneId?: ZoneId;
  localDateVariables?: FreeTextDateVariable[];
};

const formatDateWithPattern = (date: LocalDateTime, pattern: string, tz?: ZoneId, locale = "en"): string => {
  const pad = (n: number, width = 2) => n.toString().padStart(width, "0");
  const jsDate = toNativeDate(date, tz);
  const replacements: [string, () => string][] = [
    ["yyyy", () => date.year().toString()],
    ["yy", () => date.year().toString().slice(-2)],
    ["MMMM", () => new Intl.DateTimeFormat(locale, { month: "long" }).format(jsDate)],
    ["MMM", () => new Intl.DateTimeFormat(locale, { month: "short" }).format(jsDate)],
    ["MM", () => pad(date.monthValue())],
    ["ww", () => pad(date.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR))],
    ["EEEE", () => new Intl.DateTimeFormat(locale, { weekday: "long" }).format(jsDate)],
    ["EEE", () => new Intl.DateTimeFormat(locale, { weekday: "short" }).format(jsDate)],
    ["dd", () => pad(date.dayOfMonth())],
    ["HH", () => pad(date.hour())],
    ["hh", () => pad(date.hour() % 12 || 12)],
    ["mm", () => pad(date.minute())],
    ["ss", () => pad(date.second())],
    ["a", () => (date.hour() < 12 ? "AM" : "PM")],
  ];

  // Sort by length descending to match longer patterns first
  replacements.sort((a, b) => b[0].length - a[0].length);

  // Use placeholders to avoid replacing within already-replaced text
  const placeholders: string[] = [];
  let result = pattern;

  replacements.forEach(([key, getValue], index) => {
    const placeholder = `\x00${index}\x00`;
    result = result.replace(new RegExp(key, "g"), placeholder);
    placeholders[index] = getValue();
  });

  // Replace placeholders with actual values
  placeholders.forEach((value, index) => {
    result = result.replace(new RegExp(`\x00${index}\x00`, "g"), value);
  });

  return result;
};

const processLocalDateVariables = (
  html: string,
  localDateVariables: FreeTextDateVariable[] | undefined,
  context: VariableReplacementContext,
  tz?: ZoneId,
  locale = "en"
) => {
  if (!localDateVariables || localDateVariables.length === 0) return html;

  const { dashboard } = context;
  const zoneId =
    context.zoneId ??
    (dashboard?.reportOptions?.timingOptions?.timeZoneId
      ? ZoneId.of(dashboard.reportOptions.timingOptions.timeZoneId)
      : BROWSER_ZONE_ID);
  let processed = html;

  localDateVariables.forEach(variable => {
    if (!variable.name) return;

    const defaults = {
      truncateTo: DweenTimeUnitEnum.HOURS,
      offsetUnit: DweenTimeUnitEnum.HOURS,
      offset: 0,
      format: DATE_PATTERN_MINUTES,
    };

    const dateVar = { ...defaults, ...variable };

    try {
      const refTime =
        context.instantReferenceTime ??
        (context.referenceTime ? Instant.parse(context.referenceTime) : Instant.now());

      let date = refTime.atZone(zoneId).toLocalDateTime();

      date = JodaTimeUtils.truncateAndOffsetLocalDatetime(
        date,
        dateVar.truncateTo,
        dateVar.offset,
        dateVar.offsetUnit
      );

      const value = formatDateWithPattern(date, dateVar.format, tz, locale);
      const regex = new RegExp(`\\$${dateVar.name}`, "g");
      processed = processed.replace(regex, value);
    } catch (error) {
      console.warn(`Failed to process local date variable ${variable.name}:`, error);
    }
  });

  return processed;
};

/**
 * Main function to replace all variables in HTML content
 */
export const replaceVariables = (
  html: string,
  context: VariableReplacementContext,
  tz?: ZoneId,
  locale = "en"
) => {
  if (!html) return html;

  return processLocalDateVariables(html, context.localDateVariables, context, tz, locale);
};
